import { useState, useCallback } from "react";
import { chatService } from "@/services/chatService";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { deriveWorkspacePersona } from "@/config/workspacePersona";
import { UserSession } from "@/types/session";
import { ChatEntry, StreamEvent } from "@/types/chat";
import { chatEntryRoutingHintMap } from "@/config/chat.config";

export const useAgent = () => {
  const { toast } = useToast();
  const { userData } = useAuth();

  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const sendMessage = useCallback(
    async (message: string, session: UserSession, context: ChatEntry) => {
      if (!session) return;

      setIsSendingMessage(true);
      try {
        const response = await chatService.sendMessage({
          message,
          context,
          session,
          agent_routing: chatEntryRoutingHintMap[context],
          workspace_persona: deriveWorkspacePersona(userData),
        });
        return response;
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
        console.error("Failed to send message:", error);
      } finally {
        setIsSendingMessage(false);
      }
    },
    [toast, userData]
  );

  const sendMessageStream = useCallback(
    async function* (message: string, session: UserSession, context: ChatEntry): AsyncGenerator<StreamEvent> {
      if (!session) return;

      setIsSendingMessage(true);
      try {
        for await (const event of chatService.sendMessageStream({
          message,
          context,
          session,
          agent_routing: chatEntryRoutingHintMap[context],
          workspace_persona: deriveWorkspacePersona(userData),
        })) {
          yield event;
        }
      } catch (error) {
        console.error("Streaming failed, falling back to non-streaming:", error);
        try {
          const response = await chatService.sendMessage({
            message,
            context,
            session,
            agent_routing: chatEntryRoutingHintMap[context],
            workspace_persona: deriveWorkspacePersona(userData),
          });
          yield { type: "done", response };
        } catch (fallbackError) {
          toast({
            title: "Error",
            description: "Failed to send message. Please try again.",
            variant: "destructive",
          });
          console.error("Failed to send message:", fallbackError);
        }
      } finally {
        setIsSendingMessage(false);
      }
    },
    [toast, userData]
  );

  return {
    sendMessage,
    sendMessageStream,
    isSendingMessage,
  };
};
